import {
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Box,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { Question } from "@app/models/question.model";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: "16px",
  border: "1px solid #e0e0e0",
  borderRadius: theme.shape.borderRadius,
  "& .MuiTableCell-root": {
    fontSize: "14px",
    padding: "4px",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 12px",
      fontSize: "13px",
    },
  },
  "& .MuiTableCell-head": {
    fontWeight: 600,
    backgroundColor: "#f5f5f5",
  },
}));

const ClickableBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  width: "24px",
  height: "24px",
  border: `1px solid ${isSelected ? theme.palette.primary.main : "#ccc"}`,
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  margin: "0 auto",
  transition: "all 0.2s",
  backgroundColor: isSelected ? theme.palette.primary.light : "transparent",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: isSelected ? theme.palette.primary.light : "#f5f5f5",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
    color: isSelected ? theme.palette.primary.contrastText : theme.palette.primary.main,
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  display: "inline-block",
  userSelect: "text",
  "& span[style*='background-color: yellow']": {
    backgroundColor: "yellow",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

interface QuestionTableRadioProps {
  questions: Question[];
  options: string[];
  value: any;
  onChange: (selectedOption: string, question: any) => void;
  onFocus: (questionNumber: number) => void;
}

function QuestionTableRadio({
  questions,
  options,
  value = {},
  onChange,
  onFocus
}: QuestionTableRadioProps) {
  const handleBoxClick = (value: string, question: any) => {
    onChange(value.toString(), question);
  };

  const handleFocus = (questionNumber: number) => {
    onFocus(questionNumber);
  };

  return (
    <FormControl component="fieldset" fullWidth>
      <StyledTableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: "200px" }}></TableCell>
              {options.map((option, index) => (
                <TableCell key={index} align="center" style={{ minWidth: "24px" }}>
                  {option}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.map((question, rowIndex) => {
              return (
                <TableRow 
                  key={rowIndex}
                  sx={{ 
                    "&:hover": { 
                      backgroundColor: "#fafafa" 
                    }
                  }}
                >
                  <TableCell component="th" scope="row" tabIndex={0} onFocus={() => handleFocus(question.questionNumber!)}>
                    <QuestionText fontWeight="bold">{question.questionNumber!}</QuestionText>&nbsp;{question.text}
                  </TableCell>
                  {options.map((option, columnIndex) => {
                    const isSelected = value[question.questionNumber!]?.data?.textValue === option;
                    const boxId = `question-${question.questionNumber!}-${rowIndex}-${columnIndex}`;

                    return (
                      <TableCell key={columnIndex} align="center" padding="checkbox" tabIndex={0} onFocus={() => handleFocus(question.questionNumber!)}>
                        <ClickableBox
                          id={boxId}
                          role="radio"
                          aria-checked={isSelected}
                          isSelected={isSelected}
                          onClick={() => handleBoxClick(option, question)}
                        >
                          {isSelected && <CheckIcon />}
                        </ClickableBox>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </FormControl>
  );
}

export default QuestionTableRadio;
